import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ThreeStepIndicator from "./ThreeStepIndicator";
import { ordersApi } from "../services/orders";
import { setFanNew, upsertFanOrder } from "../lib/fanProfile";
import { T } from "../i18n/T";

// Tonight's tracking chips above the fan page: the last three orders this
// device placed (8h TTL), polled while the page is visible; a chip leaves the
// banner the moment the DJ resolves it. Split out of pages/RequestPage.tsx.

export function usePrevOrders({ isProfilePreview }: { isProfilePreview: boolean }) {
  const [searchParams] = useSearchParams();
  const BANNER_TTL_MS = 8 * 60 * 60 * 1000;
  const PREV_ORDERS_KEY = "rq_prev_orders";
  type PrevOrderData = {
    status: string;
    searchQuery: string;
    tier?: string | null;
    trackImage?: string | null;
    // Counter-offer fields — order-status returns them so the tracking chip
    // can show "Countered" instead of lumping counters in with "Declined".
    declineReason?: string | null;
    counterAmount?: number | null;
  };
  const prevOrderIds: string[] = (() => {
    let entries: { id: string; ts: number }[] = [];
    try { entries = JSON.parse(localStorage.getItem(PREV_ORDERS_KEY) || "[]"); } catch {}
    const incoming = searchParams.get("prev_order_id");
    if (incoming) {
      entries = entries.filter(e => e.id !== incoming);
      entries.unshift({ id: incoming, ts: Date.now() });
      entries = entries.slice(0, 3);
      localStorage.setItem(PREV_ORDERS_KEY, JSON.stringify(entries));
    }
    const valid = entries.filter(e => Date.now() - e.ts <= BANNER_TTL_MS);
    if (valid.length !== entries.length) localStorage.setItem(PREV_ORDERS_KEY, JSON.stringify(valid));
    return valid.map(e => e.id);
  })();
  const [prevOrders, setPrevOrders] = useState<Record<string, PrevOrderData>>({});

  // Banner rule (locked): chips show only requests still needing
  // attention — pending, or a live counter awaiting the fan's answer.
  // Everything the DJ resolved leaves the banner and lives in the profile
  // sheet instead; the header chip's dot points fans there.
  const chipPhase = (o: PrevOrderData): "active" | "resolved" => {
    const counterLive =
      o.declineReason === "more_money" && (o.counterAmount ?? 0) > 0;
    return o.status === "pending" || (o.status === "declined" && counterLive)
      ? "active"
      : "resolved";
  };
  const chipPhaseRef = useRef<Record<string, string>>({});

  // Any order id that lands here via ?prev_order_id also joins the device's
  // permanent registry (profile sheet history) — the banner above keeps its
  // own short list + 8h TTL for tonight's glanceables.
  useEffect(() => {
    const incoming = searchParams.get("prev_order_id");
    if (incoming) upsertFanOrder(incoming);
  }, [searchParams]);

  /** Poll previous order statuses — with an instant catch-up the moment the
   *  fan unlocks / switches back, so the tracking chips are never seconds
   *  stale when they glance (fan-profiles Phase 1). */
  useEffect(() => {
    if (isProfilePreview || !prevOrderIds.length) return;
    const poll = () => {
      prevOrderIds.forEach(id => {
        ordersApi.status(id)
          .then(row => {
            // A chip that just went active → resolved is the DJ acting while
            // the fan watches this page: it slides out of the banner, so
            // light the header chip's dot to say where it went.
            const phase = chipPhase(row);
            if (chipPhaseRef.current[id] === "active" && phase === "resolved") {
              setFanNew();
            }
            chipPhaseRef.current[id] = phase;
            setPrevOrders(prev => ({ ...prev, [id]: row }));
          })
          .catch(() => {});
      });
    };
    poll();
    const interval = setInterval(() => {
      if (document.hidden) return; // background tab — catch up on return instead
      poll();
    }, 5000);
    const onVisible = () => { if (!document.hidden) poll(); };
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) poll(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
    // Mount-only on purpose: the poll list is captured on arrival and chip
    // phases live in a ref; re-subscribing per render would stack intervals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { prevOrderIds, prevOrders, chipPhase };
}

export type PrevOrders = ReturnType<typeof usePrevOrders>;

export default function PrevOrderChips({ prev }: { prev: PrevOrders }) {
  const { prevOrderIds, prevOrders, chipPhase } = prev;
  return (
    <>
      {prevOrderIds.map((id, i) => {
        const order = prevOrders[id];
        if (!order) return null;
        // Resolved chips clear from the banner (locked) — the request
        // lives on in the profile sheet.
        if (chipPhase(order) === "resolved") return null;
        return (
          <div
            key={id}
            className="rq-prev-order-banner"
            role="button"
            tabIndex={0}
            onClick={() => { window.location.href = `/fan/requests?order=${id}`; }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") window.location.href = `/fan/requests?order=${id}`; }}
          >
            <div className="rq-prev-order-inner">
              {order.trackImage && (
                <img className="rq-prev-order-img" src={order.trackImage} alt={order.searchQuery} />
              )}
              <div className="rq-prev-order-right">
                <div className="rq-prev-order-label"><T k="prevOrderChips.requestedSong" vars={{ searchQuery: order.searchQuery }} tags={{ 0: (c) => <strong>{c}</strong> }} /></div>
                <ThreeStepIndicator
                  status={order.status as any}
                  isPriority={(order.tier as any) === "fast"}
                  isCountered={order.declineReason === "more_money" && (order.counterAmount ?? 0) > 0}
                />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
